<template>
    <div>
        <div class="w-100">
            <template v-if="hasRules">
                <!-- this doesnt use a pc-table because we'll want to be able to use drag and drop to re-order rows.
                     It might be possible to rework pc-table form scratch to support that scenario,
                     but the changes would likely require changes to the places that use it -->
                <rule-row
                    v-bind:column-options="columnOptions"
                    v-bind:rule-type="ruleType"
                    is-header />
                <template v-if="orderable">
                    <pc-orderable
                        v-model="ruleIdsInOrder"
                        v-bind:show-item-shadow-box="false"
                        v-on:update:model-value="$app.setUnsavedChanges(true)">
                        <template v-slot:default="{ itemKey }">
                            <rule-row
                                v-bind:column-options="columnOptions"
                                v-bind:rule="getRuleFromId(itemKey)"
                                v-bind:rule-type="ruleType" />
                        </template>
                    </pc-orderable>
                </template>
                <rule-row
                    v-for="(rule, index) in rules"
                    v-else
                    v-bind:key="index"
                    v-bind:column-options="columnOptions"
                    v-bind:rule="rule"
                    v-bind:rule-type="ruleType" />
            </template>
            <div
                v-else
                class="fst-italic">
                {{ emptyRuleTableMessage }}
            </div>
        </div>
    </div>
</template>
<script>
    import ruleRow from "./ruleRow.vue";
    import { RULE_TYPE } from "Types/automationTypes";
    import { EDIT_AUTOMATION_NAMESPACE, EDIT_AUTOMATION_TYPES } from "./editAutomationStore";
    import { responsiveManager } from "Responsive/manager";

    export default {
        components: { ruleRow },
        props: {
            //rules: { type: Array, required: true},
            columns: { type: Array, required: true },
            emptyRuleTableMessage: { type: String, required: true },
            ruleType: { type: String, required: true }
        },
        data() {
            return {
                width: Number.MAX_SAFE_INTEGER
            };
        },
        computed: {
            rules() {
                return this.$store.getters[EDIT_AUTOMATION_TYPES.getters.getRulesByType](this.ruleType);
            },
            ruleIdsInOrder: {
                get() {
                    return this.$store.state[EDIT_AUTOMATION_NAMESPACE].actionRuleIdsInOrder;
                },
                set(clientIds) {
                    this.$store.commit(EDIT_AUTOMATION_TYPES.mutations.reorderRules, { clientIds });
                }
            },
            rulesById() {
                const map = {};
                this.rules.forEach((rule) => {
                    map[rule.clientId] = rule;
                });
                return map;
            },
            orderable() {
                return this.ruleType === RULE_TYPE.action || this.ruleType === RULE_TYPE.programWideProject;
            },
            hasRules() {
                return Array.isArray(this.rules) && this.rules.length > 0;
            },
            columnMap() {
                const map = {};
                this.columns.forEach((col) => {
                    map[col.property] = col;
                });
                return map;
            },
            columnOptions() {
                /**
                 * Since we aren't using a table, we have to handle some of the layout ourselves.
                 * We make use of block flex containers (display:flex) to achieve this.
                 * Each row is a separate flex container, and has exactly N children (one for each column).
                 * Each column will have a width set, and the parent container will as well. Because of how block flex
                 * continaers work, if the parent width is smaller than the sum of the children,
                 * each child will get squished. However, the squish is not based on the content, so we can
                 * depend on each row doing the same.
                 *
                 * In order to calculate the actual width that will be included in the column styles, we use the
                 * hand crafted numbers below. Each column has a minium size, and a preffered size.
                 * We go in priority order, taking width from the parent to satisfy all mininums.
                 * If there's any width left, we start again taking up to the preffered width.
                 * Lastly, if there's anything left, the width is divided between the columns depending on their
                 * preferred width
                 *
                 * This is probably simpler than how html tables are layed out, but it works pretty well.
                 */
                //in order of width priority
                const columns = [
                    { min: 130, pref: 350, width: 0, break: true, ...this.columnMap.ErrorMessage },
                    { min: 130, pref: 350, width: 0, break: true, ...this.columnMap.Action },
                    { min: 25, pref: 25, width: 0, ...this.columnMap.Order },
                    { min: 130, pref: 225, width: 0, break: true, ...this.columnMap.FormulaId },
                    { min: 75, pref: 170, width: 0, ...this.columnMap.StopFurtherProcessing },
                    { min: 75, pref: 100, width: 0, ...this.columnMap.Frequency }
                ];
                //2rem + 2px if you change this, you need to change the .menu-wrapper class in ruleRow
                const menuWidth = 28;
                let remainingWidth = this.width - menuWidth;

                for (const column of columns) {
                    if (column.visible) {
                        column.width = column.min;
                        remainingWidth -= column.min;
                    }
                }

                for (const column of columns) {
                    if (column.visible && remainingWidth > 0) {
                        const addition = Math.min(remainingWidth, column.pref - column.min);

                        column.width += addition;
                        remainingWidth -= addition;
                    }
                }

                // Split the remaining width between the columns, weighting columns by their preferred width
                // This also spreads negative widths, so it shrinks columns in mobile view
                if (remainingWidth != 0) {
                    const visibleColumns = columns.filter((c) => c.visible);
                    const totalPreferredWidth = visibleColumns.reduce((sum, col) => sum + col.pref, 0);
                    columns.forEach((c) => (c.width += (remainingWidth * c.pref) / totalPreferredWidth));
                }
                const optionsMap = {};
                for (const column of columns) {
                    optionsMap[column.property] = column;
                }
                //return options in same order as original columns array
                return this.columns.filter((c) => c.visible).map((col) => optionsMap[col.property]);
            }
        },
        async mounted() {
            await this.$nextTick();
            if (typeof ResizeObserver === "function") {
                this.resizeObserver = new ResizeObserver(() => {
                    window.requestAnimationFrame(this.onResize);
                });
                this.resizeObserver.observe(this.$el);
            } else {
                responsiveManager.addResizeListener(this.onResize);
            }
        },
        methods: {
            getRuleFromId(clientId) {
                return this.rulesById[clientId];
            },
            onResize() {
                this.width = this.$el.getBoundingClientRect().width;
            }
        }
    };
</script>
